<script lang="ts" setup>
import { computed } from 'vue';
import { getBorderColor, isColorValid } from '#layers/ui/utils/colors';

const {
	size = 'tile',
	iconBackground,
	iconForeground,
} = defineProps<{
	icon?: string;
	/** Glyph color for one-color icons; full-color logos keep their own colors. */
	iconForeground?: string;
	/** Tile fill. Without one the tile uses the neutral theme surface. */
	iconBackground?: string;
	size?: 'sidebar' | 'compact' | 'tile' | 'header' | 'list';
}>();

const sizeClasses = {
	sidebar: {
		icon: 12,
		wrapper: 'size-5',
	},
	compact: {
		icon: 16,
		wrapper: 'size-7',
	},
	list: {
		icon: 18,
		wrapper: 'size-8',
	},
	tile: {
		icon: 24,
		wrapper: 'size-11',
	},
	header: {
		icon: 30,
		wrapper: 'size-14',
	},
} as const;

function validColor(color: string | undefined) {
	return color && isColorValid(color) ? color : undefined;
}

const tileBackground = computed(() => validColor(iconBackground));
const glyphColor = computed(() => validColor(iconForeground));

const tileStyle = computed(() => {
	if (!tileBackground.value) return glyphColor.value ? { color: glyphColor.value } : {};

	return {
		backgroundColor: tileBackground.value,
		borderColor: getBorderColor(tileBackground.value),
		color: glyphColor.value ?? '#ffffff',
	};
});
</script>

<template>
	<span
		class="flex shrink-0 items-center justify-center rounded-[25%] border"
		:class="[sizeClasses[size].wrapper, !tileBackground && 'border-default bg-elevated text-muted']"
		:style="tileStyle"
	>
		<slot>
			<DIcon
				v-if="icon"
				:name="icon"
				:size="sizeClasses[size].icon"
			/>
		</slot>
	</span>
</template>
